import { useContext } from "react"
import { graphql } from "gatsby"
import get from "lodash.get"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import useTranslations from "hooks/useTranslations"
import SEO from "components/seo"
import LogoCloudAnimated from "components/organisms/LogoCloudAnimated"
import {
  formatArticles,
  formatQuestionPairs,
  formatTestimonial,
} from "utils/prismic"
import { siteCountry } from "utils/env"
import { LocaleContext } from "contexts"
import FAQ from "components/organisms/somebuddy/FAQ"
import QuotesCarousel from "components/variants/QuotesCarouselSchools"
import HowDoWeHelpSchools from "components/variants/HowDoWeHelpSchools"
import Hero from "components/organisms/schools/Hero"
import DownloadForm from "components/organisms/schools/DownloadForm"
import Testimonials from "components/organisms/somebuddy/Testimonials"
import BlogPosts from "components/organisms/common/BlogPosts"
import Media from "components/organisms/somebuddy/ListHorizontal"
import Features from "components/organisms/schools/Features"
import InfoBoy from "components/organisms/schools/InfoBoy"
import InfoGirl from "components/organisms/schools/InfoGirl"
import SubscribeForm from "components/SubscribeForm"
import Report2025ReferenceSection from "components/organisms/common/Report2025ReferenceSection"
import UkSchoolsPage from "components/organisms/schools/uk"
import i18nConfig from "../../config/i18n"

const SchoolsPage = ({ data, pageContext: { currentPage } }) => {
  const { schoolPageTitle } = useTranslations()
  const { locale } = useContext(LocaleContext)

  // The global build serves the UK schools market, which needs a different
  // page entirely — prevention + response, curriculum coverage, pilot offer.
  // Finland and Sweden keep the composition below untouched.
  if (siteCountry === "global") {
    return <UkSchoolsPage />
  }

  const countryOptions = i18nConfig[siteCountry]
  const localeOptions = countryOptions[locale]
  const showNewsletterSubscription = get(
    localeOptions,
    "showNewsletterSubscription",
  )
  const prismicFAQ =
    data && data.allPrismicFaq ? data.allPrismicFaq : { edges: [] }
  const questionAnswerPairs = formatQuestionPairs(prismicFAQ)

  const prismicArticles =
    data && data.allPrismicNewsArticle
      ? data.allPrismicNewsArticle
      : { edges: [] }
  const articles = formatArticles(prismicArticles)

  const testimonials = data.allPrismicTestimonial.nodes.map((t) =>
    formatTestimonial({ id: t.uid, ...t.data }),
  )

  const FaqImage = ({ className }) => (
    <GatsbyImage
      image={getImage(data.schoolFaqImage)}
      imgStyle={{
        objectFit: "cover",
        objectPosition: "center",
      }}
      className={className}
    />
  )

  return (
    <div>
      <SEO title={schoolPageTitle} />
      <Hero />
      <HowDoWeHelpSchools />
      <InfoBoy />
      <InfoGirl />
      <BlogPosts />
      <Features />
      <QuotesCarousel />
      {siteCountry === "global" ? null : <DownloadForm />}
      <LogoCloudAnimated center currentPage={currentPage} />
      <Testimonials list={testimonials} bgColor="yellow" />
      {siteCountry === "fi" ? (
        <Report2025ReferenceSection theme="dark" />
      ) : null}
      <FAQ pairs={questionAnswerPairs} Image={FaqImage} />
      <Media items={articles} allItemsLink="/media" theme="dark" />
      {showNewsletterSubscription ? <SubscribeForm segment="schools" /> : null}
    </div>
  )
}

export default SchoolsPage

export const pageQuery = graphql`
  query ($locale: String!, $currentPage: String!) {
    allPrismicNewsArticle(
      sort: { data: { published_at: DESC } }
      limit: 3
      filter: {
        lang: { eq: $locale }
        data: { show_on_pages: { elemMatch: { page: { eq: $currentPage } } } }
      }
    ) {
      edges {
        node {
          id
          data {
            article {
              text
            }
            published_at(formatString: "DD.MM.YYYY")
            link_to_article {
              url
            }
            publisher_logo {
              url
            }
            publisher_name {
              text
            }
          }
        }
      }
    }
    allPrismicFaq(
      sort: { data: { index: ASC } }
      filter: {
        lang: { eq: $locale }
        data: { show_on_pages: { elemMatch: { page: { eq: $currentPage } } } }
      }
    ) {
      edges {
        node {
          data {
            index
            question {
              text
            }
            answer {
              richText
            }
          }
        }
      }
    }
    allPrismicTestimonial(
      limit: 2
      filter: {
        lang: { eq: $locale }
        data: { show_on_pages: { elemMatch: { page: { eq: $currentPage } } } }
      }
    ) {
      nodes {
        id
        data {
          fact_large {
            text
          }
          fact_small {
            text
          }
          logo {
            url
            alt
          }
          title {
            text
          }
        }
        uid
      }
    }
    schoolFaqImage: file(
      relativePath: { eq: "schools/AdobeStock_195624071.jpeg" }
    ) {
      childImageSharp {
        gatsbyImageData(quality: 90, width: 1200)
      }
    }
  }
`
